import CustomizeToolTip from '@src/components/atoms/tooltip';
import Info from '@src/components/icons/info';
import TriangleErrorIcon from '@src/components/icons/triangleErrorIcon';
import LoadIcon from '@src/components/icons/loadIcon';
import { errorContent, notAvailable, toolTipDescription } from '@src/constants';
import { useGetDRepStatsQuery } from '@src/store/drep/api';
import { getContentState } from '@src/utils/queryStateUtils';
import { formatAda, lovelaceToAda } from '@src/utils/dRepUtils';

interface StatisticsItemProps {
  label: string;
  value: string;
  valueStyle: string;
  hasIcon: boolean;
}
export default function StatisticsCard({ DRepId }: { DRepId: string }) {
  const dRepStatsQuery = useGetDRepStatsQuery(DRepId, {
    skip: !DRepId,
  });
  const { data: dRepStats, isLoading, refetch } = dRepStatsQuery;
  // Sticky across a retry: RTK Query clears `isError` while the refetch runs,
  // which otherwise flips the card back to a clean `-` state mid-retry.
  const { isError } = getContentState(dRepStatsQuery);
  const isRetrying = isError && dRepStatsQuery.isFetching;

  // A stat is only rendered when the backend actually returned it: on an error
  // or a missing field we show `-` rather than a `0` that reads as a real value.
  const statValue = <T,>(
    value: T | undefined | null,
    format: (statistic: T) => string
  ): string =>
    isError || value === undefined || value === null
      ? notAvailable
      : format(value);

  function StatisticItem({
    label,
    value,
    valueStyle,
    hasIcon,
  }: StatisticsItemProps) {
    const staticsToolTipDescription =
      label === 'Live Voting Power'
        ? toolTipDescription.liveVotingPower
        : toolTipDescription.influencePower;

    return (
      <div className="flex flex-col">
        <div className="body14 flex gap-2 font-medium dark:text-gray-400">
          <span>{label}</span>
          {hasIcon && (
            <>
              <Info
                className="z-20 cursor-pointer"
                data-tooltip-place="right-start"
                data-tooltip-id={label}
              />
              <CustomizeToolTip
                id={label}
                className="!max-w-[172px]"
                description={staticsToolTipDescription}
              />
            </>
          )}
        </div>
        <p
          className={`body20 font-semibold ${
            value === notAvailable ? 'dark:text-gray-400' : valueStyle
          }`}
        >
          {value}
        </p>
      </div>
    );
  }

  return (
    <div className="min-w-[200px] rounded-lg border dark:border-dark-neutral-700 dark:bg-dark-neutral-900 xl:min-w-[288px]">
      <div className="body18 h-[51px] rounded-t-lg border-b px-4 py-3 font-semibold dark:border-dark-neutral-700 dark:bg-dark-neutral-800 dark:text-white">
        Statistics
      </div>
      {isLoading ? (
        <div className="h-[297px] w-full animate-pulse rounded-b-md bg-neutral-700" />
      ) : (
        <div className="flex flex-col gap-4 p-4">
          {isError && (
            <div className="flex flex-col gap-2 rounded-lg border border-semantic-error-500 p-3">
              <p className="body14 flex items-center gap-2 font-semibold text-semantic-error-500">
                <TriangleErrorIcon className="h-4 w-4 shrink-0" />
                {errorContent.title}
              </p>
              <button
                className="body14 flex w-fit items-center gap-2 font-medium underline disabled:no-underline disabled:opacity-70 dark:text-white"
                onClick={() => {
                  refetch();
                }}
                disabled={isRetrying}
              >
                {isRetrying ? 'Retrying' : 'Try again'}
                {isRetrying && <LoadIcon className="animate-spin" />}
              </button>
            </div>
          )}
          <StatisticItem
            label="Live Voting Power"
            value={statValue(dRepStats?.liveVotingPower, (votingPower) =>
              formatAda(lovelaceToAda(votingPower))
            )}
            valueStyle="dark:text-white"
            hasIcon={true}
          />
          <StatisticItem
            label="Influence Power"
            value={statValue(dRepStats?.influence, (influence) => influence)}
            valueStyle="dark:text-semantic-success-600"
            hasIcon={true}
          />
          <StatisticItem
            label="Delegators"
            value={statValue(dRepStats?.liveDelegators, (delegators) =>
              delegators.toString()
            )}
            valueStyle="dark:text-white"
            hasIcon={false}
          />
          <div className="h-[0.5px] w-full bg-statistics-section-gradent"></div>
          <div className="flex flex-wrap justify-between gap-4">
            <StatisticItem
              label="Voted"
              value={statValue(dRepStats?.voted, (voted) => voted.toString())}
              valueStyle="dark:text-semantic-success-600"
              hasIcon={false}
            />
            <StatisticItem
              label="Not Voted"
              value={statValue(dRepStats?.notVoted, (notVoted) =>
                notVoted.toString()
              )}
              valueStyle="dark:text-semantic-error-600"
              hasIcon={false}
            />
          </div>
        </div>
      )}
    </div>
  );
}
